import { Checkbox } from '@material-ui/core';
import Button, { ButtonProps } from 'components/UI/V2/Button/Button';
import { Dropdown } from 'components/UI/V2/Dropdown/Dropdown';
import { SelectDropdownDivider } from 'components/UI/V2/SelectDropdown/Divider/SelectDropdownDivider';
import { SelectDropdownItem } from 'components/UI/V2/SelectDropdown/Item/SelectDropdownItem';
import { useSelectDropdownStyles } from 'components/UI/V2/SelectDropdown/SelectDropdown.styles';
import React from 'react';

export interface SelectDropdownOption {
    id: string;
    label: string;
}

type SelectDropdownProps = {
    label: string;
    header?: React.ReactNode;
    buttonProps?: Omit<ButtonProps, 'label' | 'onClick'>;
    options?: Array<SelectDropdownOption>;
    className?: string;
} & (
    | {
          multiple?: false;
          value: SelectDropdownOption['id'];
          onChange?: (value: SelectDropdownOption['id']) => void;
      }
    | {
          multiple: true;
          value: Array<SelectDropdownOption['id']>;
          onChange?: (value: Array<SelectDropdownOption['id']>) => void;
      }
);

export const SelectDropdown: React.FC<SelectDropdownProps> = props => {
    const classes = useSelectDropdownStyles();

    const [isOpen, setIsOpen] = React.useState(false);
    const [selected, setSelected] = React.useState<Array<SelectDropdownOption['id']>>(
        props.multiple ? props.value : [props.value]
    );

    React.useEffect(() => {
        setSelected(props.multiple ? props.value : [props.value]);
    }, [isOpen]);

    const label = React.useMemo(() => {
        if (props.multiple && props.value.length === props.options?.length) {
            return `All ${props.label}`;
        }

        if (props.multiple) {
            return `(${props.value.length}) ${props.label}`;
        }

        if (props.value) {
            return props.options?.find(option => option.id === props.value)?.label;
        }

        return props.label;
    }, [props.value, props.multiple]);

    const toggleAllHandler = () => {
        if (selected.length === props.options?.length) {
            setSelected([]);
        } else {
            setSelected(props.options?.map(option => option.id) || []);
        }
    };

    const changeHandler = (value: SelectDropdownOption['id']) => {
        if (props.multiple) {
            setSelected(prev => {
                if (prev.includes(value)) {
                    return prev.filter(v => v !== value);
                }

                return [...prev, value];
            });
        } else {
            setSelected([value]);
        }
    };

    const submitHandler = (value: SelectDropdownProps['value']) => {
        setIsOpen(false);

        if (!props.multiple) {
            return props.onChange?.(value as string);
        }

        if (Array.isArray(value)) {
            props.onChange?.(value);
        }
    };

    return (
        <Dropdown
            label={label}
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            buttonProps={{ ...props.buttonProps, label }}
        >
            <div className={classes.header}>
                <SelectDropdownItem
                    label={`All ${props.label}`}
                    className={classes.headerItem}
                    leftElement={
                        <Checkbox
                            checked={selected.length === props.options?.length}
                            className={classes.checkbox}
                        />
                    }
                    onClick={toggleAllHandler}
                />

                <SelectDropdownDivider />
            </div>

            {props.options?.map(option => (
                <SelectDropdownItem
                    key={option.id}
                    label={option.label}
                    leftElement={
                        <Checkbox
                            checked={selected.includes(option.id)}
                            className={classes.checkbox}
                        />
                    }
                    onClick={() => changeHandler(option.id)}
                />
            ))}

            <div className={classes.footer}>
                <Button
                    fullWidth
                    label="Apply"
                    variant="green"
                    onClick={() => submitHandler(selected)}
                />
            </div>
        </Dropdown>
    );
};
